import { useCallback } from "react";
import {
  useCommandPaletteStore,
  CommandPaletteOption,
  CommandPaletteMode,
  SubMenuConfig,
} from "@/store/zustand/commandPaletteStore";

// option categories are used to group options together
// the order in the enum is the display order
export enum OptionCategory {
  ISSUE_SELECTION = "issue-selection",
  GLOBAL = "global",
}

export const useCommandPalette = () => {
  const store = useCommandPaletteStore();

  const getVisibleOptions = useCallback(() => {
    let output: CommandPaletteOption[] = [];
    for (const category of Object.values(OptionCategory)) {
      if (store.optionsToShow.has(category)) {
        let options = store.options.get(category);
        options?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        options?.forEach((option) => {
          output.push(option);
        });
      }
    }
    return output;
  }, [store]);

  const registerCommand = useCallback(
    (command: string, option: CommandPaletteOption) => {
      const options = store.options.get(command) ?? [];
      options.push(option);
      store.options.set(command, options);
    },
    [store]
  );

  const unregisterCommand = useCallback(
    (command: string, option: CommandPaletteOption) => {
      const options = store.options.get(command) ?? [];
      store.options.set(
        command,
        options.filter((o) => o !== option)
      );
    },
    [store]
  );

  const registerCategory = useCallback(
    (category: string, options: CommandPaletteOption[]) => {
      store.insertOptionCategory(category, options);
    },
    [store]
  );

  const unregisterCategory = useCallback(
    (category: string) => {
      store.removeOptionCategory(category);
    },
    [store]
  );

  const showCategory = useCallback(
    (category: string) => {
      store.showOptionCategory(category);
    },
    [store]
  );

  const hideCategory = useCallback(
    (category: string) => {
      store.hideOptionCategory(category);
    },
    [store]
  );

  const open = useCallback(() => {
    store.setIsOpen(true);
  }, [store]);

  const close = useCallback(() => {
    store.setIsOpen(false);
    store.setMode(CommandPaletteMode.DEFAULT);
    store.setSubMenuConfig(null);
  }, [store]);

  const setMode = useCallback(
    (mode: CommandPaletteMode) => {
      store.setMode(mode);
    },
    [store]
  );

  const setSubMenuConfig = useCallback(
    (config: SubMenuConfig | null) => {
      store.setSubMenuConfig(config);
    },
    [store]
  );

  return {
    registerCommand,
    unregisterCommand,
    registerCategory,
    unregisterCategory,
    showCategory,
    hideCategory,
    open,
    close,
    getVisibleOptions,
    isOpen: store.isOpen,
    setMode,
    mode: store.mode,
    setSubMenuConfig,
    subMenuConfig: store.subMenuConfig,
  };
};
